// The earned-autonomy ladder on the Quality page (ADR-0014, docs/cockpit/spec.md): for each kind of work LAMP does,
// the level each Task using it runs at (Manual → Ask → Auto) and why it is capped, the highest it has earned in
// LAMP's tests, and this business's counts. Evidence, not scores: counts only. Lower and Raise propose a Tree Change
// with its preview; only an Owner confirms one, and Raise is offered only up to the earned level. When this
// business's own track record lowered a kind of work (M3-21c), it says so with the evidence in counts, and LAMP's
// Suggestion to raise it back waits here for an Owner to accept or dismiss. Staff see it read-only.
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  type Api,
  type AutonomyLadder as Ladder,
  type AutonomyLevel,
  type LadderMove,
  type TaskClassLadder,
  type TreeChange,
} from "../api.js";
import { useWords, type Words } from "../i18n/words.js";
import { ChangePreview, appliedNotice } from "../settings/ChangePreview.js";

export type LadderApi = Pick<
  Api,
  | "autonomyLadder"
  | "proposeAutonomy"
  | "confirmChange"
  | "testChangeWithAgent"
  | "acceptRaiseBack"
  | "dismissRaiseBack"
>;

const LEVELS: readonly AutonomyLevel[] = ["manual", "ask", "auto"];

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.settingsRefused(error.code)
    : w.settingsUnreachable;

function TaskClassRow({
  row,
  days,
  owner,
  busy,
  onMove,
  onRaiseBack,
  onNotNow,
}: {
  row: TaskClassLadder;
  days: number;
  owner: boolean;
  busy: boolean;
  onMove: (move: LadderMove) => void;
  onRaiseBack: (ladder: boolean) => void;
  onNotNow: () => void;
}) {
  const w = useWords();
  const [alsoLadder, setAlsoLadder] = useState(true);
  const headingId = `ladder-${row.taskClass}`;
  const { counts } = row;
  const lowered = row.trackRecord?.cap ? row.trackRecord.notices[0] : undefined;
  const suggestion = row.trackRecord?.suggestion;
  return (
    <li className="item" aria-labelledby={headingId}>
      <h3 id={headingId}>{w.ladderName(row.name, row.taskClass)}</h3>
      <ol className="ladder" aria-label={w.ladderRungs}>
        {LEVELS.map((level) => (
          <li key={level} className={level === row.ceiling.level ? "tag tag-good" : "tag tag-plain"}>
            {w.autonomyLevel(level)}
          </li>
        ))}
      </ol>
      <p>
        {row.ceiling.evaluatedOn === null
          ? w.ladderNotTested
          : w.ladderEarned(w.autonomyLevel(row.ceiling.level), row.ceiling.evaluatedOn)}
      </p>
      <ul className="plain-list">
        {row.tasks.map((t) => (
          <li key={t.task}>
            <span data-pack="">{t.name}</span>
            {": "}
            {w.ladderRunsAt(w.autonomyLevel(t.level))}
            {t.cappedBy ? ` (${w.ladderCap(t.cappedBy)})` : ""}
          </li>
        ))}
      </ul>
      {lowered ? (
        <p className="alert" role="note">
          {w.ladderLowered(w.autonomyLevel(lowered.level), lowered.reason)}
        </p>
      ) : null}
      {suggestion ? (
        <div role="group" aria-label={w.ladderRaiseBack(w.autonomyLevel(suggestion.to))}>
          <p>{suggestion.reason}</p>
          {owner ? (
            <p>
              <label>
                <input type="checkbox" checked={alsoLadder} onChange={(e) => setAlsoLadder(e.currentTarget.checked)} />{" "}
                {w.ladderRaiseBackAlso}
              </label>{" "}
              <button type="button" className="btn" disabled={busy} onClick={() => onRaiseBack(alsoLadder)}>
                {w.ladderRaiseBack(w.autonomyLevel(suggestion.to))}
              </button>{" "}
              <button type="button" className="btn" disabled={busy} onClick={onNotNow}>
                {w.ladderNotNow}
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="small">
        {w.ladderCounts(
          days,
          counts.drafted,
          counts.approvedUnchanged,
          counts.changed,
          counts.rejected,
          counts.correctedLater,
        )}
      </p>
      {owner ? (
        <p>
          {row.lower ? (
            <button type="button" className="btn" disabled={busy} onClick={() => row.lower && onMove(row.lower)}>
              {w.ladderLower(w.autonomyLevel(row.lower.to))}
            </button>
          ) : null}{" "}
          {row.raise ? (
            <button type="button" className="btn" disabled={busy} onClick={() => row.raise && onMove(row.raise)}>
              {w.ladderRaise(w.autonomyLevel(row.raise.to))}
            </button>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}

export function AutonomyLadder({ api, owner }: { api: LadderApi; owner: boolean }) {
  const w = useWords();
  const [ladder, setLadder] = useState<Ladder>();
  const [failed, setFailed] = useState<string>();
  const [preview, setPreview] = useState<TreeChange>();
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let live = true;
    api.autonomyLadder().then(
      (found) => live && setLadder(found),
      (error: unknown) => live && setFailed(problemOf(error, w)),
    );
    return () => {
      live = false;
    };
  }, [api, w]);
  useEffect(load, [load]);

  const act = async (step: () => Promise<void>) => {
    setBusy(true);
    setProblem(undefined);
    setNotice(undefined);
    try {
      await step();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };
  const move = (m: LadderMove) =>
    void act(async () => {
      setPreview(await api.proposeAutonomy(m));
    });
  const confirm = (change: TreeChange, migrate: boolean) =>
    void act(async () => {
      const applied = await api.confirmChange(change.id, migrate);
      setPreview(undefined);
      setNotice(appliedNotice(applied, w));
      load();
    });
  const raiseBack = (taskClass: string, ladder: boolean) =>
    void act(async () => {
      const raised = await api.acceptRaiseBack(taskClass, ladder);
      setNotice(w.ladderRaisedBack(w.autonomyLevel(raised.to)));
      if (raised.change) setPreview(raised.change);
      load();
    });
  const notNow = (taskClass: string) =>
    void act(async () => {
      await api.dismissRaiseBack(taskClass);
      setNotice(w.ladderNotSuggested);
      load();
    });
  const testWithAgent = (change: TreeChange, milliCredits: number) =>
    void act(async () => {
      const tested = await api.testChangeWithAgent(change.id, milliCredits);
      setPreview((shown) => (shown?.id === tested.id ? tested : shown));
    });

  return (
    <section className="section" aria-labelledby="ladder-heading">
      <h2 id="ladder-heading">{w.ladderHeading}</h2>
      {failed ? (
        <p role="alert">{failed}</p>
      ) : !ladder ? (
        <p role="status">{w.ladderLoading}</p>
      ) : (
        <>
          <p className="muted">{owner ? w.ladderIntro : w.ladderReadOnly}</p>
          {notice ? <p role="status">{notice}</p> : null}
          {problem ? (
            <p className="alert" role="alert">
              {problem}
            </p>
          ) : null}
          {preview ? (
            <ChangePreview
              change={preview}
              owner={owner}
              busy={busy}
              onConfirm={confirm}
              onDismiss={() => setPreview(undefined)}
              onTestWithAgent={testWithAgent}
            />
          ) : null}
          {ladder.taskClasses.length === 0 ? <p className="muted">{w.ladderNothing}</p> : null}
          <ul className="plain-list">
            {ladder.taskClasses.map((row) => (
              <TaskClassRow
                key={row.taskClass}
                row={row}
                days={ladder.days}
                owner={owner}
                busy={busy}
                onMove={move}
                onRaiseBack={(also) => raiseBack(row.taskClass, also)}
                onNotNow={() => notNow(row.taskClass)}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
